import React, { useMemo } from "react";

function normalizeLots(item = {}) {
  if (Array.isArray(item.lots) && item.lots.length > 0) {
    return item.lots.map((lot) => ({
      expirationDate: lot.expirationDate || "",
      quantity: Number(lot.quantity || 0),
    }));
  }

  return [
    {
      expirationDate: item.expirationDate || "",
      quantity: Number(item.quantityOnHand || 0),
    },
  ];
}

function lotTotal(lots = []) {
  return lots.reduce((sum, lot) => sum + Number(lot.quantity || 0), 0);
}

function earliestExpiration(lots = []) {
  const dates = lots
    .map((lot) => lot.expirationDate)
    .filter(Boolean)
    .sort();

  return dates[0] || "—";
}

function locationLabel(locationId = "") {
  if (locationId === "centralSupply") return "Central Supply";
  if (locationId === "onsiteStock") return "On Site Supply";

  return locationId || "Unknown";
}

export default function CentralSupplyPage({
  styles,
  stockItems = [],
  stockSearch,
  setStockSearch,
  categoryFilter,
  setCategoryFilter,
  locationFilter = "all",
  setLocationFilter,
  sortMode = "az",
  setSortMode,
  stockCategories = [],
  onAddItem,
  onReceiveStock,
  onImportSpreadsheet,
  importing,
  onEditItem,
  onDeleteItem,
  onManageCategories,
  onAdjustQuantity,
}) {
  const consolidatedItems = useMemo(() => {
    const map = {};

    stockItems.forEach((item) => {
      const key = `${item.itemName || ""}_${item.supplier || ""}_${
        item.itemNumber || item.vendorPartNumber || ""
      }`;

      const lots = normalizeLots(item);
      const qty = lotTotal(lots);
      const locationId = item.locationId || "unknown";

      if (!map[key]) {
        map[key] = {
          ...item,
          lotsByLocation: {},
          allLots: [],
          totalQuantityOnHand: 0,
          centralSupplyQty: 0,
          onsiteStockQty: 0,
          deployedQty: 0,
          centralSupplyBin: "",
          onsiteStockBin: "",
          parLevel: Number(item.parLevel || 0),
          reorderLevel: Number(item.reorderLevel || 0),
          locations: new Set(),
          sourceRecords: [],
        };
      }

      map[key].sourceRecords.push({
  ...item,
  lots,
  quantityOnHand: qty,
});
      map[key].totalQuantityOnHand += qty;
      map[key].allLots.push(
        ...lots.map((lot) => ({
          ...lot,
          locationId,
          locationLabel: locationLabel(locationId),
        }))
      );

      if (!map[key].lotsByLocation[locationId]) {
        map[key].lotsByLocation[locationId] = [];
      }

      map[key].lotsByLocation[locationId].push(...lots);
      map[key].locations.add(locationId);

      if (locationId === "centralSupply") {
        map[key].centralSupplyQty += qty;
        if (item.shelfBin) map[key].centralSupplyBin = item.shelfBin;
      } else if (locationId === "onsiteStock") {
        map[key].onsiteStockQty += qty;
        if (item.shelfBin) map[key].onsiteStockBin = item.shelfBin;
      } else {
        map[key].deployedQty += qty;
      }

      map[key].parLevel = Math.max(
        Number(map[key].parLevel || 0),
        Number(item.parLevel || 0)
      );

      map[key].reorderLevel = Math.max(
        Number(map[key].reorderLevel || 0),
        Number(item.reorderLevel || 0)
      );
    });

    return Object.values(map).map((item) => ({
      ...item,
      locations: Array.from(item.locations),
      earliestExpiration: earliestExpiration(item.allLots),
    }));
  }, [stockItems]);

  const locationOptions = useMemo(() => {
    return Array.from(
      new Set(stockItems.map((item) => item.locationId).filter(Boolean))
    ).sort();
  }, [stockItems]);

  const filteredItems = useMemo(() => {
    const q = String(stockSearch || "").trim().toLowerCase();

    let results = consolidatedItems.filter((item) => {
      const itemName = String(item.itemName || "").toLowerCase();

const matchesSearch =
  !q || itemName.includes(q);

      const matchesCategory =
        categoryFilter === "all" || item.category === categoryFilter;

      const matchesLocation =
        locationFilter === "all" || item.locations.includes(locationFilter);

      return matchesSearch && matchesCategory && matchesLocation;
    });

    results = [...results].sort((a, b) => {
        if (q) {
    const aName = String(a.itemName || "").toLowerCase();
    const bName = String(b.itemName || "").toLowerCase();

    const aStarts = aName.startsWith(q);
    const bStarts = bName.startsWith(q);

    if (aStarts && !bStarts) return -1;
    if (!aStarts && bStarts) return 1;
  }
      const nameA = String(a.itemName || "");
      const nameB = String(b.itemName || "");

      if (sortMode === "za") return nameB.localeCompare(nameA);
      if (sortMode === "qtyLow") {
        return Number(a.totalQuantityOnHand || 0) - Number(b.totalQuantityOnHand || 0);
      }
      if (sortMode === "qtyHigh") {
        return Number(b.totalQuantityOnHand || 0) - Number(a.totalQuantityOnHand || 0);
      }

      return nameA.localeCompare(nameB);
    });

    return results;
  }, [
    consolidatedItems,
    stockSearch,
    categoryFilter,
    locationFilter,
    sortMode,
  ]);

  function getStatus(item) {
    const total = Number(item.totalQuantityOnHand || 0);
    const reorder = Number(item.reorderLevel || 0);

    if (total <= 0) return { label: "Out", color: "#b91c1c" };
    if (reorder > 0 && total <= reorder) return { label: "Low", color: "#b45309" };

    return { label: "Good", color: "#15803d" };
  }

  return (
    <div style={styles.panel}>
      <div style={styles.panelHeader}>
        <div>
          <h2 style={styles.panelTitle}>Inventory Management</h2>
          <p style={styles.subtitle}>
            View total stock across central supply, on-site supply, and deployed kits.
          </p>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button onClick={onAddItem} style={styles.secondaryButton}>
            + Add Item
          </button>         

          <label style={styles.secondaryButton}>
            {importing ? "Importing..." : "Import Spreadsheet"}
            <input
              type="file"
              accept=".xlsx,.xls"
              style={{ display: "none" }}
              onChange={onImportSpreadsheet}
            />
          </label>

          <button onClick={onManageCategories} style={styles.secondaryButton}>
            Manage Categories
          </button>

          <button onClick={onReceiveStock} style={styles.primaryButton}>
            + Move / Receive Supplies
          </button>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 180px 180px 160px",
          gap: 10,
          margin: "12px 0 14px",
          alignItems: "center",
        }}
      >
        <input
          value={stockSearch}
          onChange={(e) => setStockSearch(e.target.value)}
          placeholder="Search inventory..."
          style={styles.searchInput}
        />

        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          style={styles.filterSelect}
        >
          <option value="all">All Categories</option>
          {stockCategories.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>

        <select
          value={locationFilter}
          onChange={(e) => setLocationFilter?.(e.target.value)}
          style={styles.filterSelect}
        >
          <option value="all">All Locations</option>
          {locationOptions.map((locationId) => (
            <option key={locationId} value={locationId}>
              {locationLabel(locationId)}
            </option>
          ))}
        </select>

        <select
          value={sortMode}
          onChange={(e) => setSortMode?.(e.target.value)}
          style={styles.filterSelect}
        >
          <option value="az">A–Z</option>
          <option value="za">Z–A</option>
          <option value="qtyLow">Qty Low</option>
          <option value="qtyHigh">Qty High</option>
        </select>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Item</th>
              <th style={styles.th}>Category</th>
              <th style={styles.th}>Total</th>
              <th style={styles.th}>Central</th>
              <th style={styles.th}>On Site</th>
              <th style={styles.th}>Earliest Expiration</th>
              <th style={styles.th}>Unit</th>
              <th style={styles.th}>PAR / Reorder</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {filteredItems.map((item) => {
              const status = getStatus(item);

              return (
                <tr key={item.id}>
                  <td style={styles.td}>
                    <strong>{item.itemName}</strong>
                    <div style={{ fontSize: 11, color: "#64748b" }}>
                      {item.supplier || "No supplier"}
                    </div>
                  </td>

                  <td style={styles.td}>{item.category || "—"}</td>

                  <td style={styles.td}>
                    <strong>{item.totalQuantityOnHand ?? 0}</strong>
                  </td>

                  <td style={styles.td}>{item.centralSupplyQty ?? 0}</td>
                  <td style={styles.td}>{item.onsiteStockQty ?? 0}</td>

                  <td style={styles.td}>{item.earliestExpiration || "—"}</td>

                  <td style={styles.td}>{item.unitType || "each"}</td>

                  <td style={styles.td}>
                    {item.parLevel ?? 0} / {item.reorderLevel ?? 0}
                  </td>

                  <td style={styles.td}>
                    <span
                      style={{
                        padding: "4px 8px",
                        borderRadius: 999,
                        background: `${status.color}20`,
                        color: status.color,
                        fontWeight: 700,
                        fontSize: 11,
                      }}
                    >
                      {status.label}
                    </span>
                  </td>

                  <td style={styles.td}>
                    <div style={{ display: "flex", gap: 6 }}>

<button
  style={styles.smallButton}
  onClick={() => onEditItem(item)}
>
  View / Edit
</button>

                      <button
                        style={styles.smallDangerButton}
                        onClick={() => onDeleteItem(item)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}

            {filteredItems.length === 0 && (
              <tr>
                <td style={styles.td} colSpan="11">
                  No matching inventory items found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}